import {WidgetData} from '../../../core/shared/widget/widget-data';
import {WidgetFactory} from '../../../core/shared/widget/service/widget.factory';
import {WidgetDataParam} from '../../../core/shared/widget/widget-data-param';
import {FilterCriteria} from '../../../core/shared/filter/api/filter.criteria';
import {Sorting} from '../../../core/shared/misc/sorting';
import {QlQueryDto} from '../../../core/shared/ql/data/dto/ql-query.dto';
import {buildCwCuratedWidgetForm} from './cw-widget-form.util';

const WIDGET_ALIAS = 'config.cw.CwCaseTypeExchangeSkeleton.widget';

/**
 * Builds the "Skeleton" config table widget for cw.CwCaseTypeExchangeSkeleton (design G.5
 * node id `exchangeSkeletons`) — the FreeMarker structure template (body only) of a CaseType
 * (M2-WP1 §1). Read-mostly inspection table: rows come from the alpha-cw-taa-bundle-generator
 * SMART-Seed bundle, never hand-added; `content` itself is read-only in the UI (seed-bundle-
 * only edits, PO J2-1 — wp4-fe-ruckfragen-resolved.md Q7).
 *
 * CW-EXCHANGE-V2 (design §2.3/§3.2): there is exactly ONE active skeleton per CaseType, covering
 * every operation via `<#if operation == …>` switches — a scoped table showing more than one row
 * therefore means a misconfiguration the assembler will reject, which is why the table stays a
 * table instead of collapsing into a single-record widget.
 *
 * WidgetFactory.createWidgetTableQl on an explicit, hand-curated QlQueryDto (root
 * cw.CwCaseTypeExchangeSkeleton aliased `e`, one LEFT join to the owning caseType) plus a
 * curated WidgetForm. Deliberately EXCLUDES the large `content`/`namespaceCatalog` TEXT columns
 * from the table grid — design G.5 wants a dedicated read-only XML-highlighted viewer for
 * `content` in the node-view detail (a Slice-C/fe-component concern), not a grid cell.
 *
 * @param caseTypeId optional cw.CwCaseType id to scope the table to one CaseType (e.g. embedded
 *   under that CaseType's node/detail); omit for the unscoped, all-CaseTypes inspection table.
 */
export function buildCwCaseTypeExchangeSkeletonTableWidget(caseTypeId?: number): WidgetData {
    const qlQuery: QlQueryDto = {
        name: WIDGET_ALIAS,
        type: 'view',
        start: {
            name: 'cw.CwCaseTypeExchangeSkeleton',
            as: 'e',
            type: 'entity',
            fields: [
                {name: 'id'},
                {name: 'alias'},       // Alias
                {name: 'description'}, // Beschreibung (provenance)
            ],
            joins: [
                {
                    type: 'entity', name: 'caseType', as: 'ct', joinType: 'left',
                    fields: [{name: 'id'}, {name: 'name'}], // Vorgangstyp (resolved NAME)
                },
            ],
        },
        fixedFilters: caseTypeId != null
            ? [FilterCriteria.create('e.caseTypeId', FilterCriteria.cOperatorEqual, caseTypeId)]
            : [],
        sortings: [new Sorting('e.alias', true)],
    };

    /**
     * AI:
     * Status: "in progress"
     * Type: Widget
     * SubType: Runtime
     * Reason: CwCaseTypeExchangeSkeleton config table — the FreeMarker structure templates per
     *   CaseType, curated columns (Vorgangstyp, Alias, Beschreibung); the large `content` field
     *   is intentionally not a grid column.
     */
    const widgetData = WidgetFactory.createWidgetTableQl(
        WIDGET_ALIAS,
        'Skeletons',
        qlQuery,
        WidgetDataParam.create('widgetForm', buildCwCaseTypeExchangeSkeletonWidgetForm()),
    );
    widgetData.noDataText = 'Keine Skeletons konfiguriert';
    return widgetData;
}

function buildCwCaseTypeExchangeSkeletonWidgetForm() {
    return buildCwCuratedWidgetForm([
        {field: 'ct.name', uiLabel: 'Vorgangstyp'},
        {field: 'e.alias', uiLabel: 'Alias'},
        {field: 'e.description', uiLabel: 'Beschreibung'},
    ]);
}
